/**
 * Copy + formatting helpers for the workshop cancel flow.
 *
 * COPY FRAMING: customers are moved to the chosen workshop at the same price and
 * emailed the new details. No refund or "can decline" language.
 */
import type { CancelWorkshopErrorCode } from '../../../../api/workshops';

/**
 * Format a venue-local wall-clock string (e.g. `2026-05-10T17:00:00-04:00`) for
 * display WITHOUT timezone conversion. The string carries the venue's offset, so
 * `new Date(...)` would re-anchor it to the viewer's zone — instead we read the
 * wall-clock components straight off the leading `YYYY-MM-DDTHH:MM` portion.
 *
 * Returns e.g. "Sun, May 10 · 5:00p". Falls back to the raw input if it doesn't
 * match the expected shape.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function formatLocalStartsAt(localStartsAt: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(localStartsAt);
  if (!m) return localStartsAt;
  const [, y, mo, d, hh, mm] = m;
  const year = Number(y);
  const monthIdx = Number(mo) - 1;
  const day = Number(d);
  const hours = Number(hh);
  const minutes = Number(mm);
  if (monthIdx < 0 || monthIdx > 11) return localStartsAt;

  // Weekday via a UTC Date built from the same wall-clock components (no zone
  // shift — we only read getUTCDay()). Already venue-local-safe (regex off the
  // wall clock), so this is not a BON-386 instant-display violation.
  // eslint-disable-next-line no-restricted-syntax -- zero-shift weekday from wall-clock components, not an instant display
  const weekday = WEEKDAYS[new Date(Date.UTC(year, monthIdx, day)).getUTCDay()];
  const ampm = hours >= 12 ? 'p' : 'a';
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  const clock =
    minutes === 0 ? `${h12}${ampm}` : `${h12}:${String(minutes).padStart(2, '0')}${ampm}`;
  return `${weekday}, ${MONTHS[monthIdx]} ${day} · ${clock}`;
}

/**
 * Human message for a cancel refusal `code`. Nothing changed on any of them.
 * Unknown codes fall back to the server-provided message.
 */
export function cancelErrorMessage(code: string, serverMessage: string): string {
  const map: Record<CancelWorkshopErrorCode, string> = {
    SOURCE_NOT_FOUND: 'This workshop no longer exists — refresh and try again.',
    SOURCE_NOT_DISABLED: 'Disable this workshop before cancelling it.',
    PENDING_SALES: 'A reservation or payment on this workshop hasn’t settled yet.',
    BOOKINGS_CHANGED: 'A booking was just added to this workshop. Try again.',
    TARGET_REQUIRED: 'Pick the workshop to move the booked customers into.',
    TARGET_NOT_FOUND: 'That workshop no longer exists — pick another.',
    TARGET_IS_SOURCE: 'Pick a different workshop to move customers into.',
    TARGET_NOT_BOOKABLE: 'That workshop is no longer taking bookings — pick another.',
    TARGET_REGION_MISMATCH: 'That workshop is in a different region — pick one in this region.',
    TARGET_PRICE_MISMATCH: 'That workshop is a different price — pick a matching one.',
    NO_CAPACITY: 'That workshop no longer has enough seats — pick another.',
    ALREADY_REBOOKED:
      'One of these bookings was just moved by someone else — refresh and try again.',
    NOT_CONFIRMED: 'One of these bookings is no longer confirmed — refresh and try again.',
    CONCURRENT_MODIFICATION: 'Someone else was changing these bookings. Try again.',
  };
  return map[code as CancelWorkshopErrorCode] ?? serverMessage;
}
